import React from "react";
import { createContext, useState, useMemo, useContext, useLayoutEffect, useEffect } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { syncDesignPathname } from "../../lib/bkDesignNavigationHistory";
import { saveBkDesignOrderContext } from "../../lib/bkDesignOrderContext";
import { useBkScanAutoLoad } from "../../hooks/useBkScanAutoLoad";
import { useBkSceneResume } from "../../hooks/useBkSceneResume";
import { useBkSceneAutosave } from "../../hooks/useBkSceneAutosave";
import { clearOrderCache } from "../../lib/bkSceneCache";
import { ThreeScene } from "../viewer/ThreeScene";
import ToolRenderer from "../viewer/ToolRenderer";
import { DesignShortcuts } from "./DesignShortcuts";
import { CursorManager } from "../viewer/CursorManager";
import ShortcutsHelp from "./ShortcutsHelp";
import SaveDraftDialog from "./drafts/SaveDraftDialog";
import LoadDraftDialog from "./drafts/LoadDraftDialog";
import LoadingOverlay from "./LoadingOverlay";
import useEditorStore from "../../stores/useEditorStore";
import useWorkflowStore from "../../stores/useWorkflowStore";
import {
  PATHS,
  SCAN_PROCESS_STEPS,
  WORKFLOW_CONFIG,
  getWorkflowTypeForStep,
  normalizeWorkflowType,
  isFinalizationPath,
  isPreviewStepPath,
  syncWorkflowFromPathname,
} from "../../constants/flowConfig";
import StepHeader from "./StepHeader";
import { loadBkDesignOrderContext } from "../../lib/bkDesignOrderContext";
import { useStepCheckpointSync, clearAllStepCheckpoints } from "../../../features/step-checkpoints";

// 1. Create a context to hold our layout setters
const LayoutContext = createContext(null);

// 2. Create the main layout component
const DesignLayout = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // If /design is opened directly with ?sales_order&customer, treat URL as source of truth.
  // This prevents stale bk_design_order_context from a previous order from being reused.
  // Optional bk_main_process_intent (BenX deep links) must match useWorkflowStore contract.
  useLayoutEffect(() => {
    const so = searchParams.get('sales_order');
    const cu = searchParams.get('customer');
    if (so && cu) saveBkDesignOrderContext(so, cu);

    if (!so || !cu) return;

    const raw = searchParams.get('bk_main_process_intent');
    if (!raw) return;
    const allowed = new Set(['modify_rectification', 'modify_locking', 'fresh']);
    if (!allowed.has(raw)) return;

    useWorkflowStore.getState().actions.setBkMainProcessIntent(raw);
  }, [searchParams]);

  // Deep links (BenX portal → /design/...?sales_order=&customer=) never hit PatientDashboard,
  // so patient + scan meta must be hydrated here or `useBkScanAutoLoad` has nothing to load.
  const designHydrateKey = searchParams.toString();
  useEffect(() => {
    const ctx = loadBkDesignOrderContext();
    const so = searchParams.get("sales_order")?.trim() || ctx?.sales_order;
    const cu = searchParams.get("customer")?.trim() || ctx?.customer;
    if (!so || !cu) return;
    useWorkflowStore
      .getState()
      .actions.fetchPatientData(so, cu)
      .catch(() => {
        /* store holds patientDataError */
      });
  }, [designHydrateKey]);

  // Keep a lightweight navigation history for BK step hooks (e.g. Finalization → Rectification).
  useLayoutEffect(() => {
    syncDesignPathname(location.pathname);
    const synced = syncWorkflowFromPathname(location.pathname);
    if (synced) {
      const { setCurrentWorkflow, setCurrentStep } = useWorkflowStore.getState().actions;
      setCurrentWorkflow(synced.workflow);
      setCurrentStep(synced.step);
    }
  }, [location.pathname]);
  const isFinalizationStep = isFinalizationPath(location.pathname);
  const isPreviewStepPage = isPreviewStepPath(location.pathname);

  // Full-scene restore from the client cache after a page reload (must run
  // before the raw-scan auto-loader so it can claim the mesh for this step).
  useBkSceneResume();

  // Continuously mirror the current step's scene into the client cache so a
  // reload restores the step the user is actively working on.
  useBkSceneAutosave();

  // Step-wise mesh + tool session restore on route changes
  useStepCheckpointSync();

  // Restore scan mesh after refresh once files meta is loaded (with fetchPatientData)
  useBkScanAutoLoad();

  // Loading states
  const isLoading = useEditorStore(state => state.isLoading);
  const loadingProgress = useEditorStore(state => state.loadingProgress);
  const rightPanelVisible = useEditorStore(state => state.rightPanelVisible);

  const handleStartFromScratch = () => {
    clearAllStepCheckpoints();
    const scratchCtx = loadBkDesignOrderContext();
    if (scratchCtx) {
      clearOrderCache(`${scratchCtx.sales_order}:${scratchCtx.customer}`);
    }
    const editor = useEditorStore.getState();
    editor.setActiveTool('none');
    editor.setUploadedModelData(null);
    editor.clearToolStates?.();

    const wf = useWorkflowStore.getState();
    wf.actions.setBkMainProcessIntent('fresh');
    wf.actions.setScanAutoLoaded(false);

    const ctx = loadBkDesignOrderContext();
    if (ctx) {
      // Preparing Scan step itself doesn't fetch; trigger the same API hydration as a fresh entry.
      wf.actions.fetchPatientData(ctx.sales_order, ctx.customer).catch(() => {
        // store keeps error; avoid unhandled promise rejection
      });
    }
    navigate(PATHS[SCAN_PROCESS_STEPS.PREPARING_SCAN]);
  };

  const currentStep = useWorkflowStore((state) => state.currentStep);
  const currentWorkflow = useWorkflowStore((state) => state.currentWorkflow);
  const stepCompletion = useWorkflowStore((state) => state.stepCompletion);
  const resolvedWorkflow =
    getWorkflowTypeForStep(currentStep) ?? normalizeWorkflowType(currentWorkflow, currentStep);
  const workflowSteps = WORKFLOW_CONFIG[resolvedWorkflow]?.steps ?? [];

  // State for each "slot" in our UI
  const [toolbarContent, setToolbarContent] = useState(null);
  const [infoPanelContent, setInfoPanelContent] = useState(null);
  const [footerContent, setFooterContent] = useState(null);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    setToolbar: setToolbarContent,
    setInfoPanel: setInfoPanelContent,
    setFooter: setFooterContent,
  }), []);

  return (
    <LayoutContext.Provider value={contextValue}>
      <div
        className={
          'flex h-screen min-h-0 flex-col bg-[#fafafa] text-[#171717]'
        }
      >
        {currentStep ? (
          <StepHeader
            currentStep={currentStep}
            workflowSteps={workflowSteps}
            stepCompletion={stepCompletion}
          />
        ) : null}

        <div
          className={` relative
             flex min-h-0 flex-1 overflow-hidden m-1.5 mt-0 bg-white`}
        >
          {/* Left side - Toolbar OR finalization / preview step panel */}
          {isFinalizationStep || isPreviewStepPage ? (
            <div className="flex w-[70%] min-w-0 flex-col overflow-hidden border-r border-black/[0.08] bg-white max-xl:w-[30%] max-md:h-[60vh] max-md:w-full max-md:flex-col">
              <Outlet />
            </div>
          ) : (
            <div className="absolute z-20 top-0 left-0 flex m-1 shrink-0 flex-col items-center justify-center [tap-highlight-color:transparent] max-md:w-[60px] max-[480px]:h-[60px] max-[480px]:w-full max-[480px]:flex-row max-[480px]:justify-around max-[480px]:p-[0.3rem]">
              {toolbarContent}
            </div>
          )}

          {/* Center - Always the 3D Scene (persistent) */}
          <div
            className={`z-10 ${isFinalizationStep || isPreviewStepPage
                ? 'relative flex w-[40%] min-w-0 flex-col bg-[#fafafa] max-xl:w-[55%] max-md:h-[40vh] max-md:w-full'
                : 'relative min-w-0 flex-1 overflow-hidden'
              }`}
          >
            <main
              className={
                isFinalizationStep || isPreviewStepPage
                  ? 'relative m-3 min-h-0 flex-1 overflow-hidden bg-white'
                  : 'absolute inset-0 z-[1] bg-[#fafafa] transition-opacity duration-200 motion-reduce:transition-none'
              }
            >
              <div className="relative h-full w-full">
                <ThreeScene>
                  <ToolRenderer />
                  {!isFinalizationStep && !isPreviewStepPage && <DesignShortcuts />}
                  <CursorManager />
                  <ShortcutsHelp />
                  {!isFinalizationStep && !isPreviewStepPage && <Outlet />}
                </ThreeScene>

                {isFinalizationStep && (
                  <div className="pointer-events-auto absolute bottom-3.5 right-3.5 z-40 flex max-w-[min(100%-32px,320px)] flex-col items-end gap-2 text-right">
                    <button
                      type="button"
                      className="m-0 cursor-pointer rounded-md border border-black/[0.12] bg-white px-3.5 py-2 text-[13px] font-medium leading-snug tracking-tight text-[#171717] shadow-sm touch-manipulation transition-colors [tap-highlight-color:transparent] hover:border-black/[0.18] hover:bg-[#fafafa] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0070f3] focus-visible:outline-offset-2 active:bg-[#f4f4f5] motion-reduce:transition-none"
                      onClick={handleStartFromScratch}
                    >
                      Start Design from Scratch
                    </button>
                  </div>
                )}
              </div>
            </main>

            {!isFinalizationStep && !isPreviewStepPage && (
              <div
                className="absolute bottom-0 right-0 top-0 z-30 transition-all duration-300 ease-out bg-bg-primary rounded-lg"
                style={{
                  width: rightPanelVisible ? '18%' : '0px',
                  minWidth: rightPanelVisible ? '300px' : '0px',
                  opacity: rightPanelVisible ? 1 : 0,
                  transform: rightPanelVisible ? 'translateX(0)' : 'translateX(14px)',
                  pointerEvents: rightPanelVisible ? 'auto' : 'none',
                }}
              >
                {infoPanelContent}
              </div>
            )}
          </div>

          {/* Full-workspace loading overlay — must sit above toolbar (z-20) and canvas */}
          {isLoading && <LoadingOverlay progress={loadingProgress} />}
        </div>

        {/* Footer */}

        {footerContent && !isFinalizationStep && !isPreviewStepPage && (
          <div className="flex h-14 shrink-0 items-center justify-between border-t border-black/[0.08] bg-white px-5 [tap-highlight-color:transparent] max-md:px-4">
            {footerContent}
          </div>
        )}

        {/* Draft Management Dialogs */}
        <SaveDraftDialog />
        <LoadDraftDialog />
      </div>
    </LayoutContext.Provider>
  );
};

// 3. Create a custom hook to easily access the slot setters
export const useLayoutSlots = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    // Return a no-op context to prevent errors during initial render
    return {
      setToolbar: () => { },
      setInfoPanel: () => { },
      setFooter: () => { },
    };
  }
  return context;
};

export default DesignLayout;